// Module entrances on the work pages (from the Jitter compositions).
//
// Each section's entrance is built once as paused animations: its modules sit
// in their start state ("primed") until the section scrolls in, then play.
//   first section   "intro": every module moves in from its side while it
//                   resizes out of that edge, rounded corners kept (an image
//                   stays centred in its growing box, an outlined box resizes
//                   its outline); headlines slide in line by line
//   This is the one rule for every work page (the Sports cover was the model):
//   new pages only list their modules, they never get their own animation.
//   other sections  "slide": modules rise (or drop) half their height and fade
//
// Modules are marked in the markup (src/work/Modules.tsx):
//   data-block="fill"     solid block (or one that holds an image layer: "image")
//   data-block="image"    block with an image layer (.w-layer)
//   data-block="outline"  box drawn by its outline only
//   data-block="media"    video
//   data-text             a text inside a block (headline or body copy)
//   data-from             force the side a module enters from

export const EASE = {
    natural: "cubic-bezier(.8,0,.2,1)", // Jitter "Natural"
    slow: "cubic-bezier(0,0,0,1)", // Jitter "Slow down"
    linear: "linear", // Jitter "None"
    snap: "cubic-bezier(.2,.9,.25,1)", // cursor arrow flip
}
// Seconds and design px (Jitter canvas 1680x1080); distances scale with the viewport height.
export const T = {
    move: 1.16,
    moveBy: 100,
    fill: 0.8,
    image: 0.8,
    textDelay: 0.36,
    head: 0.5,
    headStagger: 0.05,
    bodyDelay: 0.52,
    body: 0.45,
    bodyStagger: 0.04,
    bodyTravel: 0.7,
    enter: 0.7,
    enterBy: 0.5,
    enterLate: 0.08,
    arrow: 0.28,
    landing: 0.2, // pause before the first entrance after arriving on a page
}

/** ms an entrance waits at most for its images */
const IMAGE_WAIT = 1500

type Kind = "fill" | "image" | "outline" | "media"
type Block = { el: HTMLElement; kind: Kind; zone: string; w: number; h: number; big: boolean; radius: string }

const px = (v: number) => `${Math.round(v * 100) / 100}px`
const inset = (t: number, r: number, b: number, l: number, rad: string) => `inset(${t}px ${r}px ${b}px ${l}px round ${rad})`

/** Top-level modules of a section, as laid out right now (hidden ones skipped). */
function measure(section: HTMLElement) {
    const box = section.getBoundingClientRect()
    const s = Math.max(0.4, innerHeight / 1080)
    const blocks: Block[] = []
    const walk = (root: Element) => {
        for (const child of Array.from(root.children) as HTMLElement[]) {
            if (getComputedStyle(child).display === "none") continue
            let kind = child.getAttribute("data-block") as Kind | null
            // an image block whose image isn't shown at this size (the video on
            // phones) is a plain group: look inside it
            if (kind === "image") {
                // (a video inset on a panel: the inset, poster and video together, is its layer)
                const layer = child.querySelector(":scope > .w-layer, :scope > .w-boxed")
                if (!layer || getComputedStyle(layer).display === "none") kind = null
            }
            if (!kind) {
                walk(child)
                continue
            }
            const r = child.getBoundingClientRect()
            const cx = (r.left + r.width / 2 - box.left) / Math.max(1, box.width)
            const top = (r.top - box.top) / Math.max(1, box.height)
            let zone = child.getAttribute("data-from")
            if (!zone) zone = kind === "outline" ? "bottom" : cx < 0.2 ? "left" : top < 0.35 ? "top" : "bottom"
            blocks.push({
                el: child,
                kind,
                zone,
                w: r.width,
                h: r.height,
                big: r.width * r.height > box.width * box.height * 0.25,
                radius: getComputedStyle(child).borderRadius || "0px",
            })
        }
    }
    walk(section)
    const pos = new Map(blocks.map((b) => [b, b.el.getBoundingClientRect()]))
    blocks.sort((a, b) => pos.get(a)!.left - pos.get(b)!.left || pos.get(a)!.top - pos.get(b)!.top)
    return { blocks, s }
}

// A headline that wraps is one element: split it into its visual lines so
// every line gets its own masked slide.
const ORIGINAL = new WeakMap<Element, string>()
function splitLines(p: HTMLElement): HTMLElement[] | null {
    if (ORIGINAL.has(p)) p.textContent = ORIGINAL.get(p)!
    if (p.children.length) return null
    const text = p.textContent || ""
    if (!text.trim()) return null
    const words = text.trim().split(/\s+/)
    ORIGINAL.set(p, text)
    p.textContent = ""
    const spans = words.map((w, i) => {
        const sp = document.createElement("span")
        sp.textContent = w
        p.appendChild(sp)
        if (i < words.length - 1) p.appendChild(document.createTextNode(" "))
        return sp
    })
    const rows: string[][] = []
    let lastTop = -Infinity
    spans.forEach((sp, i) => {
        const t = sp.offsetTop
        if (t > lastTop + 2) {
            rows.push([])
            lastTop = t
        }
        rows[rows.length - 1].push(words[i])
    })
    p.textContent = ""
    if (rows.length < 2) {
        p.textContent = text
        ORIGINAL.delete(p)
        return null
    }
    return rows.map((r) => {
        const line = document.createElement("span")
        line.style.display = "block"
        line.textContent = r.join(" ")
        p.appendChild(line)
        return line
    })
}

type Scene = { anims: Animation[]; primed: boolean; played: boolean }

export function createEntrances(speed = 1) {
    const k = 1 / Math.max(0.1, speed)
    const scenes = new Map<HTMLElement, Scene>()
    let landing = true // the first entrance after arriving waits T.landing

    const scene = (el: HTMLElement) => {
        let S = scenes.get(el)
        if (!S) scenes.set(el, (S = { anims: [], primed: false, played: false }))
        return S
    }
    const run = (S: Scene, el: Element, frames: Keyframe[], o: { duration: number; delay?: number; easing: string; pseudoElement?: string }) => {
        const a = el.animate(frames, { fill: "backwards", ...o, duration: o.duration * 1000 * k, delay: (o.delay || 0) * 1000 * k })
        a.pause()
        S.anims.push(a)
    }
    const clear = (S: Scene) => {
        S.anims.forEach((a) => a.cancel())
        S.anims = []
    }

    function text(S: Scene, t: HTMLElement, base: number, s: number) {
        let kids = (Array.from(t.children) as HTMLElement[]).filter((c) => c.offsetHeight > 0)
        if (!kids.length) kids = [t]
        const fs = parseFloat(getComputedStyle(kids[0]).fontSize) || 16
        const head = fs >= 26 * Math.min(1, s)
        if (head) kids = kids.flatMap((c) => splitLines(c) || [c])
        kids.forEach((kid, i) => {
            const h = kid.offsetHeight
            if (head) {
                run(
                    S,
                    kid,
                    [
                        { translate: `0px ${px(-h)}`, clipPath: `inset(${px(h)} 0px 0px 0px)` },
                        { translate: "0px 0px", clipPath: "inset(0px 0px 0px 0px)" },
                    ],
                    { duration: T.head, delay: base + T.textDelay + i * T.headStagger, easing: EASE.slow }
                )
            } else {
                const lh = parseFloat(getComputedStyle(kid).lineHeight) || fs * 1.3
                const delay = base + T.bodyDelay + i * T.bodyStagger
                run(S, kid, [{ translate: `0px ${px(-T.bodyTravel * lh)}` }, { translate: "0px 0px" }], { duration: T.body, delay, easing: EASE.slow })
                run(S, kid, [{ opacity: 0 }, { opacity: 1 }], { duration: T.body, delay, easing: EASE.linear })
            }
        })
    }

    function intro(S: Scene, section: HTMLElement, base: number) {
        const { blocks, s } = measure(section)
        for (const b of blocks) {
            const d = T.moveBy * s
            const from = b.zone === "left" ? `${px(-d)} 0px` : b.zone === "right" ? `${px(d)} 0px` : b.zone === "top" ? `0px ${px(-d)}` : `0px ${px(d)}`
            run(S, b.el, [{ translate: from }, { translate: "0px 0px" }], { duration: T.move, delay: base, easing: EASE.slow })
            const full = inset(0, 0, 0, 0, b.radius)
            const grow = { duration: T.fill, delay: base, easing: EASE.natural }
            if (b.kind === "outline") {
                // the outline (drawn on ::after) resizes downward, keeping its corners,
                // and fades in over the start of the resize; the contents are never clipped
                run(S, b.el, [{ height: "0px" }, { height: px(b.h) }], { ...grow, pseudoElement: "::after" })
                run(S, b.el, [{ opacity: 0 }, { opacity: 1 }], { duration: T.fill * 0.45, delay: base, easing: EASE.linear, pseudoElement: "::after" })
            } else {
                // every other module resizes out of the edge it comes from (its rounded
                // corners kept): from the left it widens to the right, from the right to
                // the left, otherwise it grows downward
                const start = b.zone === "left" ? inset(0, b.w, 0, 0, b.radius) : b.zone === "right" ? inset(0, 0, 0, b.w, b.radius) : inset(0, 0, b.h, 0, b.radius)
                run(S, b.el, [{ clipPath: start }, { clipPath: full }], grow)
                // an image stays centred in the growing box, as a cover image in a box
                // that really resizes would (no unmasking, no zoom)
                const layer = b.kind === "image" ? b.el.querySelector(":scope > .w-layer, :scope > .w-boxed") : null
                if (layer) {
                    const centre = b.zone === "left" ? `${px(-b.w / 2)} 0px` : b.zone === "right" ? `${px(b.w / 2)} 0px` : `0px ${px(-b.h / 2)}`
                    run(S, layer, [{ translate: centre }, { translate: "0px 0px" }], grow)
                }
            }
            b.el.querySelectorAll<HTMLElement>("[data-text]").forEach((t) => text(S, t, base, s))
        }
    }

    function slide(S: Scene, section: HTMLElement, dir: number) {
        for (const b of measure(section).blocks) {
            const delay = b.big ? T.enterLate : 0
            run(S, b.el, [{ translate: `0px ${px(dir * T.enterBy * b.h)}` }, { translate: "0px 0px" }], { duration: T.enter, delay, easing: EASE.slow })
            run(S, b.el, [{ opacity: 0 }, { opacity: 1 }], { duration: T.enter, delay, easing: EASE.linear })
        }
    }

    return {
        /** build the entrance, paused at its start */
        prime(el: HTMLElement, kind: "intro" | "slide", dir: number) {
            const S = scene(el)
            clear(S)
            if (kind === "intro") intro(S, el, landing ? T.landing : 0)
            else slide(S, el, dir)
            S.primed = true
        },
        play(el: HTMLElement) {
            const S = scene(el)
            if (!S.primed) return
            S.primed = false
            S.played = true
            landing = false
            // an image that isn't there yet would only appear halfway, at its final
            // place, and the block would look masked instead of resized: the
            // entrance waits for the images it shows (at most IMAGE_WAIT)
            const anims = S.anims
            const go = () => {
                if (S.anims === anims) anims.forEach((a) => a.play())
            }
            const shown = Array.from(el.querySelectorAll("img")).filter((i) => i.getClientRects().length > 0)
            const pending = shown.filter((i) => !i.complete || !i.naturalWidth)
            if (!pending.length) return go()
            pending.forEach((i) => (i.loading = "eager"))
            Promise.race([Promise.all(pending.map((i) => i.decode().catch(() => {}))), new Promise((r) => setTimeout(r, IMAGE_WAIT))]).then(go)
        },
        state: (el: HTMLElement) => ({ primed: scene(el).primed, played: scene(el).played }),
        /** drop everything (page left) */
        /** drop everything (page left); keep: a section whose entrance goes on
         *  (it continues as the next page's opening section) */
        dispose(keep?: Set<HTMLElement>) {
            scenes.forEach((S, el) => !keep?.has(el) && clear(S))
            scenes.clear()
        },
    }
}
